import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { RosterUser } from '@/api/models';
import { useDeviceRosterQuery, usePinLoginMutation } from '@/api/queries/auth';
import { PATH } from '@/router/PATH';
import { getRoleHomePath } from '@/router/roleHome';
import {
    BRANCH_ARCHIVED,
    clearDeviceSession,
    clearSignOutReason,
    getDeviceBranch,
    getSignOutReason,
} from '@/utils';
import {
    pinAttemptMessage,
    usePinAttemptError,
} from '@/components/pinPad/usePinAttemptError';
import { PIN_LENGTH, SIGN_OUT_NOTICE_KEYS } from './sellerLoginMeta';

/**
 * Drives the two-step seller sign-in: pick a seller from the branch roster, then
 * tap or type a 4-digit PIN. On a correct PIN the seller session is persisted
 * (by the mutation) and we land on the POS. A failed attempt is classified by
 * its code: a lockout (pad held until the seller backs out and re-selects —
 * there's no `Retry-After` to count down), a rate limit (pad held, counted
 * down from `Retry-After`) or a generic wrong PIN (pad stays live). All logic
 * lives here so the step views stay render-only.
 */
const PIN_ERROR_KEYS = {
    lockout: 'sellerLogin.pinLocked',
    rateLimited: 'sellerLogin.pinRateLimited',
    invalid: 'sellerLogin.invalidPin',
};

export const useSellerLogin = () => {
    const navigate = useNavigate();
    const roster = useDeviceRosterQuery();
    const pinLogin = usePinLoginMutation();

    const [selectedUser, setSelectedUser] = React.useState<RosterUser | null>(
        null,
    );
    // SEL-35: kept across a trip to the PIN step and back, so the roster can
    // refocus the same tile instead of resetting to the first seller.
    const [lastPickedUserId, setLastPickedUserId] = React.useState<
        string | null
    >(null);
    const [pin, setPin] = React.useState('');
    // SEL-23: why the previous session on this device ended. Only a session the
    // server revoked (the seller signed in on another sale point) is explained;
    // the notice stays on the roster until someone picks a seller or the
    // terminal is re-enrolled.
    const [signOutReason, setSignOutReason] = React.useState(getSignOutReason);
    const signOutNoticeKey = signOutReason
        ? SIGN_OUT_NOTICE_KEYS[signOutReason]
        : null;

    const branchName = getDeviceBranch()?.name ?? '';

    // Memoized so an empty or refused roster doesn't hand `focusUserId` a new
    // array (and a new identity to depend on) every render.
    const rosterUsers = React.useMemo<RosterUser[]>(
        () =>
            roster.data && roster.data.success
                ? roster.data.response.items
                : [],
        [roster.data],
    );
    const rosterLoading = roster.isLoading;
    const rosterFailure =
        roster.data && !roster.data.success ? roster.data.error : null;
    const rosterError = roster.isError || rosterFailure !== null;
    // SEL-19: an archived branch is not a transient roster hiccup — retrying
    // will never populate the list, so it reads as its own message. The key is
    // always set (the step only shows it when `rosterError`), so the
    // render-only step can call `t` on it without a fallback of its own.
    const rosterErrorKey =
        rosterFailure?.code === BRANCH_ARCHIVED
            ? 'sellerLogin.branchArchived'
            : 'sellerLogin.rosterError';

    // SEL-35: who the roster should focus — the previously picked seller if
    // they're still on it, the first seller otherwise (first load, or the
    // previous pick left the roster). Resolved here, not in the render-only
    // step, so the View only ever binds an already-decided ID.
    const focusUserId = React.useMemo(() => {
        if (
            lastPickedUserId &&
            rosterUsers.some((user) => user.id === lastPickedUserId)
        ) {
            return lastPickedUserId;
        }
        return rosterUsers[0]?.id ?? null;
    }, [lastPickedUserId, rosterUsers]);

    const pinError = usePinAttemptError(pinLogin.data);
    const pinErrorMessage = pinAttemptMessage(pinError, PIN_ERROR_KEYS);
    // Held while the server has told us not to try again: a rate limit
    // (until its countdown ends) or a lockout (no countdown — cleared only
    // by backing out and re-selecting the seller, which resets the
    // mutation). Nothing sent while held: no digit entry while locked out or
    // submitting (bos-seller#52), whether tapped or typed.
    const isPinHeld =
        pinError?.kind === 'rateLimited' || pinError?.kind === 'lockout';

    // Rulebook §13: a synchronous lock, taken before the request and released
    // once it settles. `pinLogin.isPending` only turns true on the next
    // render, and every request counts toward the lockout, so a PIN must
    // never be sent twice (bos-seller#84). Every key handler checks it too:
    // a key landing before that render must not change the entry being
    // sent.
    const submitLockRef = React.useRef(false);

    const submit = React.useCallback(
        async (user: RosterUser, pinValue: string) => {
            if (submitLockRef.current) {
                return;
            }
            submitLockRef.current = true;
            try {
                const result = await pinLogin.mutateAsync({
                    userId: user.id,
                    pin: pinValue,
                });
                if (result.success) {
                    // Route by role: a seller sells (POS), a branch admin
                    // manages (the API 403s a branch admin's token on the
                    // sale endpoint).
                    navigate(getRoleHomePath(result.response.user.role), {
                        replace: true,
                    });
                } else {
                    // Wrong PIN: clear the pad so the next attempt starts
                    // fresh; the error stays visible until the operator taps
                    // again.
                    setPin('');
                }
            } finally {
                submitLockRef.current = false;
            }
        },
        [pinLogin, navigate],
    );

    const selectUser = React.useCallback(
        (user: RosterUser) => {
            setSelectedUser(user);
            setLastPickedUserId(user.id);
            setPin('');
            pinLogin.reset();
            clearSignOutReason();
            setSignOutReason(null);
        },
        [pinLogin],
    );

    const back = React.useCallback(() => {
        setSelectedUser(null);
        setPin('');
        pinLogin.reset();
    }, [pinLogin]);

    const pressDigit = React.useCallback(
        (digit: string) => {
            if (
                !selectedUser ||
                pinLogin.isPending ||
                submitLockRef.current ||
                isPinHeld ||
                pin.length >= PIN_LENGTH
            ) {
                return;
            }
            // Tapping after a failed attempt starts a clean entry.
            if (pinLogin.data && !pinLogin.data.success) {
                pinLogin.reset();
            }
            // Built from the rendered entry rather than inside a `setPin`
            // updater: React may run an updater twice (StrictMode does), and
            // the submit it started would then send the PIN twice. Each key
            // or tap is its own event, rendered before the next one arrives.
            const next = pin + digit;
            setPin(next);
            if (next.length === PIN_LENGTH) {
                void submit(selectedUser, next);
            }
        },
        [selectedUser, pinLogin, isPinHeld, pin, submit],
    );

    const pressBackspace = React.useCallback(() => {
        if (pinLogin.isPending || submitLockRef.current || isPinHeld) {
            return;
        }
        if (pinLogin.data && !pinLogin.data.success) {
            pinLogin.reset();
        }
        setPin((current) => current.slice(0, -1));
    }, [pinLogin, isPinHeld]);

    // Esc on the PIN step: clear a non-empty entry, or back out to the
    // roster on an already-empty one — layered the same way Esc is layered
    // everywhere else in the app. Blocked mid-verify, matching the on-screen
    // Back button; unlike digit entry, still allowed while rate-limited or
    // locked, since backing out doesn't need the server.
    const pressEscape = React.useCallback(() => {
        if (pinLogin.isPending || submitLockRef.current) {
            return;
        }
        if (pin.length > 0) {
            setPin('');
            pinLogin.reset();
        } else {
            back();
        }
    }, [pin, pinLogin, back]);

    const reEnroll = React.useCallback(() => {
        clearSignOutReason();
        clearDeviceSession();
        navigate(PATH.ENROLL, { replace: true });
    }, [navigate]);

    return {
        branchName,
        selectedUser,
        focusUserId,
        rosterUsers,
        rosterLoading,
        rosterError,
        rosterErrorKey,
        signOutNoticeKey,
        pin,
        pinError,
        pinErrorMessage,
        isPinHeld,
        isSubmitting: pinLogin.isPending,
        selectUser,
        back,
        pressDigit,
        pressBackspace,
        pressEscape,
        reEnroll,
    };
};

export type SellerLoginLogic = ReturnType<typeof useSellerLogin>;
